import * as React from 'react';
import { InspectionPoint, rng, SafetyInspectionFacility } from '../../../../amorg-common';
import { createFacilityInspection, deleteFacilityInspection, getAvailableFacilityInspectionPoints } from '../../../../api/safetyInspectionClientApi';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { Modal } from '../../../../designSystem/Modal';
import { TooltipWrapper } from '../../../../designSystem/TooltipWrapper';
import { Button } from '../../../../designSystem/button/Button';
import { AnimatedCollapsibleContent } from '../../../../designSystem/collapsible/AnimatedCollapsibleContent';
import { colors } from '../../../../designSystem/colors/color';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../../../designSystem/compositeComponents/ListRow';
import { Icon } from '../../../../designSystem/icons/Icons';
import { getFacilityTypeIcon } from '../../../../utils/facilityHelper';

type InspectionPointSelectorModalProps = {
    activityId: string,
    locationId: string,
    facility: SafetyInspectionFacility,
    showModal: boolean,
    onUpdate: () => void,
    onClose: () => void;
};
export function InspectionPointSelectorModal (props: InspectionPointSelectorModalProps): JSX.Element
{
    const [loading, setLoading] = React.useState(true);
    const [expandedIds, setExpandedIds] = React.useState<string[]>([]);
    const [availableInspectionPoints, setAvailableInspectionPoints] = React.useState<InspectionPoint[]>([]);

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            updateAvailableInspections();
            setExpandedIds([]);
            setLoading(false);
        };
        if (props.showModal)
            getData();
    }, [props.showModal]);

    async function updateAvailableInspections ()
    {
        const typeInspections = await getAvailableFacilityInspectionPoints(props.facility.facilityId);
        const inUseInspections = props.facility.inspectionPoints.map(ip => ip.inspectionPoint);

        for (const iui of inUseInspections)
        {
            const ip = typeInspections.find(x => x.id === iui.id);
            if (!ip)
                typeInspections.push(iui);
        }
        setAvailableInspectionPoints(typeInspections);
    }

    async function handleClickAddFacilityInspection (inspectionPointId: string)
    {
        setLoading(true);
        await createFacilityInspection(props.activityId, props.facility.facilityId, inspectionPointId, rng.getRandomId());
        await updateAvailableInspections();
        await props.onUpdate();
        setLoading(false);
    }

    async function handleClickDeleteFacilityInspection (id: string)
    {
        setLoading(true);
        await deleteFacilityInspection(props.activityId, id);
        await updateAvailableInspections();
        await props.onUpdate();
        setLoading(false);
    }

    return (
        <Modal
            title='Rediger kontrolpunkter'
            showModal={props.showModal}
            onCancel={props.onClose}
            content={
                <>
                    <LoadingPageOverlay loading={loading} />
                    {availableInspectionPoints &&
                        <Flex flexDirection='column' gap={8}>
                            <Flex alignItems='center'>
                                <Icon name={getFacilityTypeIcon(props.facility.facilityType.id)} size='medium' />
                                {props.facility.name} ({props.facility.facilityType.name})
                            </Flex>

                            <div>
                                {availableInspectionPoints.map(i =>
                                {

                                    const result = props.facility.inspectionPoints.find(ip => ip.inspectionPoint.id === i.id)?.inspectionResult;
                                    const canEdit = result === undefined || (result.status === 'pending' && result.comment === '');

                                    const inspectionPoint = props.facility.inspectionPoints.find(ip => ip.inspectionPoint.id === i.id);

                                    return (
                                        <>
                                            <ListRow key={i.id}
                                                onCollapse={(expanded) =>
                                                {
                                                    expanded
                                                        ? setExpandedIds([...expandedIds, i.id])
                                                        : setExpandedIds(expandedIds.filter(id => id !== i.id));
                                                }}>
                                                <Flex justifyContent='stretch'>
                                                    <Flex alignItems='center' gap={4}>
                                                        {i.title}
                                                        {!canEdit &&
                                                            <TooltipWrapper
                                                                tooltip={<span>Kontrolpunkter kan ikke fjernes hvis status er godkendt eller ikke-godkendt, en bemærkning er indsat eller billeder er tilføjet.</span>}>
                                                                <Icon size='small' name='survey_question' color={colors.text.normal} />
                                                            </TooltipWrapper>
                                                        }
                                                    </Flex>
                                                    <Flex justifyContent='flex-end' style={{ flexGrow: 1 }}>
                                                        {inspectionPoint
                                                            ? <Button variant='destructive' text='Fjern' disabled={!canEdit} onClick={() => handleClickDeleteFacilityInspection(inspectionPoint.inspectionResult.id)} />
                                                            : <Button variant='filled' text='Tilføj' disabled={!canEdit} onClick={() => handleClickAddFacilityInspection(i.id)} />
                                                        }
                                                    </Flex>
                                                </Flex>
                                            </ListRow>
                                            <AnimatedCollapsibleContent open={expandedIds.includes(i.id)}>
                                                <ListRow sizeVariant='fat'>
                                                    <i>{i.instructions}</i>
                                                </ListRow>
                                            </AnimatedCollapsibleContent>
                                        </>
                                    );
                                })}
                            </div>
                        </Flex>
                    }
                </>
            }
        />
    );
}